/**
 * useFloorGroupsData - Hook to load floor data from Floor groups
 *
 * Retrieves Floor groups from the project state and reconstructs
 * DetectedFloor objects from their saved properties.
 */

import { useCallback } from 'react';
import { isMeaningfulCeiling, loadFloorFootprint, type LevelPlanes } from '@pointorama/potree-shared';
import { GroupType } from '../../types/graphqlTypes';
import { useFloorProperties } from '../../modules/Properties/integration/annotations/FloorProperties';
import { useAnnotationTree } from './useAnnotationContext';

/** Subset of DetectedFloor from potree_fork */
interface DetectedFloorPlane {
  height: number;
  center: { x: number; y: number; z: number };
  normal: { x: number; y: number; z: number };
  pointCount: number;
  confidence: number;
  bounds?: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  };
}

interface FloorGroupData {
  groupIdentifier: string;
  name: string;
  floor: DetectedFloorPlane;
  ceiling?: DetectedFloorPlane;
  /** The floor's own persisted XY footprint corners, if stored. */
  footprintCorners?: Array<{ x: number; y: number; z: number }>;
}

/**
 * Hook to load floor data from Floor groups in the project
 */
export const useFloorGroupsData = (project?: { id: string }) => {
  const { getFloorPlanePosition, getFloorPlaneNormal, getCeilingPlanePosition, getCeilingPlaneNormal } = useFloorProperties(project?.id || '');
  const annotationContextAnnotations = useAnnotationTree();
  // Extract groups from annotation context (groups are top-level items with 'annotations' property)
  const groups = annotationContextAnnotations.filter((item) => 'annotations' in item) as { type: string; identifier: string; name: string }[];

  /**
   * Load detailed floor group data including both floor and ceiling
   * Useful for reconstruction or editing
   */
  const loadFloorGroupsData = useCallback((): FloorGroupData[] => {
    const floorGroups = groups.filter((group) => group.type === GroupType.Floor);

    if (floorGroups.length === 0) {
      return [];
    }

    const floorGroupData: (FloorGroupData | null)[] = floorGroups.map((group) => {
      const floorPlanePosition = getFloorPlanePosition(group.identifier);
      const floorPlaneNormal = getFloorPlaneNormal(group.identifier);

      if (!floorPlanePosition || !floorPlaneNormal) {
        return null;
      }

      // Create default bounds around the center point (10m x 10m)
      const boundsSize = 10;
      const floor: DetectedFloorPlane = {
        height: floorPlanePosition.z,
        center: {
          x: floorPlanePosition.x,
          y: floorPlanePosition.y,
          z: floorPlanePosition.z,
        },
        normal: {
          x: floorPlaneNormal.x,
          y: floorPlaneNormal.y,
          z: floorPlaneNormal.z,
        },
        bounds: {
          min: {
            x: floorPlanePosition.x - boundsSize / 2,
            y: floorPlanePosition.y - boundsSize / 2,
            z: floorPlanePosition.z - 0.1,
          },
          max: {
            x: floorPlanePosition.x + boundsSize / 2,
            y: floorPlanePosition.y + boundsSize / 2,
            z: floorPlanePosition.z + 0.1,
          },
        },
        pointCount: 0,
        confidence: 1.0,
      };

      const ceilingPlanePosition = getCeilingPlanePosition(group.identifier);
      const ceilingPlaneNormal = getCeilingPlaneNormal(group.identifier);

      let ceiling: DetectedFloorPlane | undefined;
      // An unset ceiling persists as {0,0,0} — only a ceiling meaningfully
      // above the floor counts (using it as-is collapses wall extrusion).
      if (ceilingPlanePosition && ceilingPlaneNormal && isMeaningfulCeiling(floorPlanePosition.z, ceilingPlanePosition.z)) {
        ceiling = {
          height: ceilingPlanePosition.z,
          center: {
            x: ceilingPlanePosition.x,
            y: ceilingPlanePosition.y,
            z: ceilingPlanePosition.z,
          },
          normal: {
            x: ceilingPlaneNormal.x,
            y: ceilingPlaneNormal.y,
            z: ceilingPlaneNormal.z,
          },
          pointCount: 0,
          confidence: 1.0,
        };
      }

      let footprintCorners: FloorGroupData['footprintCorners'];
      try {
        footprintCorners = loadFloorFootprint(group.identifier) ?? undefined;
      } catch {
        // Property service not ready — consumers fall back to the global area.
      }

      const result: FloorGroupData = {
        groupIdentifier: group.identifier,
        name: group.name,
        floor,
        ceiling,
        footprintCorners,
      };

      return result;
    });

    return floorGroupData.filter((data): data is FloorGroupData => data !== null).sort((a, b) => a.floor.height - b.floor.height);
  }, [groups, getFloorPlanePosition, getFloorPlaneNormal, getCeilingPlanePosition, getCeilingPlaneNormal]);

  /**
   * Minimal per-level plane snapshot for the LevelBounds resolver
   * (resolveLevelBounds / levelZRange from potree-shared).
   */
  const loadLevelPlanes = useCallback((): LevelPlanes[] => {
    return loadFloorGroupsData().map((group) => ({
      groupIdentifier: group.groupIdentifier,
      floorZ: group.floor.height,
      ceilingZ: group.ceiling?.height ?? null,
    }));
  }, [loadFloorGroupsData]);

  return {
    loadFloorGroupsData,
    loadLevelPlanes,
  };
};
